import { template as _$template } from "solid-js/web";
import { delegateEvents as _$delegateEvents } from "solid-js/web";
import { createComponent as _$createComponent } from "solid-js/web";
import { style as _$style } from "solid-js/web";
import { effect as _$effect } from "solid-js/web";
var _tmpl$ = /*#__PURE__*/_$template(`<div class=touch-nav style=position:absolute;right:12px;bottom:96px;display:flex;flex-direction:column;gap:8px;z-index:40;pointer-events:auto><button aria-label="Zoom in">+</button><button aria-label="Zoom out">−</button><button aria-label="Tilt up">▲</button><button aria-label="Tilt down">▼`);
/**
 * TouchNavComponent — on-screen zoom + tilt for touch devices.
 *
 * WHY THIS EXISTS (Brockmann 2026-08-31): pinch-zoom is unreliable inside the
 * iOS WKWebView that hosts this globe. The app already carries that scar —
 * ContentView.swift:836 and IntelHubView.swift:131 both cite the "hard to Zoom"
 * directive and the Cesium WKWebView pinch-gesture conflict. Buttons sidestep
 * the gesture layer entirely: every action here is a single tap.
 *
 * TILT uses the standard Cesium orbit pattern — lookAt(target, HeadingPitchRange)
 * then release with lookAtTransform(IDENTITY). That pivots around the GROUND POINT
 * the operator is looking at, which is what "tilt" means on a map. The naive
 * camera.lookUp() would swing the view at the camera instead and can end up
 * staring at empty sky.
 *
 * Pitch is clamped to [-89, -8] degrees so the horizon cannot flip over.
 */

import { Show } from "solid-js";
import { useCesium } from "../cesium/useCesium";
const MIN_PITCH = -89;
const MAX_PITCH = -8;
const TILT_STEP = 8;
const ZOOM_FACTOR = 0.35;
export function TouchNav(props) {
  const {
    viewer
  } = useCesium();

  /** Ground point at screen centre, or null if the ray misses the globe. */
  const groundTarget = v => {
    const canvas = v.scene.canvas;
    const centre = new Cesium.Cartesian2(canvas.clientWidth / 2, canvas.clientHeight / 2);
    // Prefer real geometry (3D tiles) so tilt pivots on the building/road under
    // the crosshair, not on the ellipsoid far below it.
    const picked = v.scene.pickPosition ? v.scene.pickPosition(centre) : null;
    if (picked && Cesium.defined(picked)) return picked;
    return v.camera.pickEllipsoid(centre, v.scene.globe.ellipsoid) ?? null;
  };
  const zoom = dir => {
    const v = viewer();
    if (!v || v.isDestroyed()) return;
    // Scale the step by altitude so one tap feels the same at 500 m and 50 km.
    const h = v.camera.positionCartographic.height;
    const amount = Math.max(25, h * ZOOM_FACTOR);
    dir > 0 ? v.camera.zoomIn(amount) : v.camera.zoomOut(amount);
    v.scene.requestRender();
  };
  const tilt = deltaDeg => {
    const v = viewer();
    if (!v || v.isDestroyed()) return;
    const target = groundTarget(v);
    if (!target) return; // looking at space — nothing to orbit around

    const cam = v.camera;
    const range = Cesium.Cartesian3.distance(cam.positionWC, target);
    const heading = cam.heading;
    const currentPitchDeg = Cesium.Math.toDegrees(cam.pitch);
    const nextPitchDeg = Math.min(MAX_PITCH, Math.max(MIN_PITCH, currentPitchDeg + deltaDeg));
    cam.lookAt(target, new Cesium.HeadingPitchRange(heading, Cesium.Math.toRadians(nextPitchDeg), range));
    // CRITICAL: release the lookAt transform, otherwise every later camera
    // operation stays locked to this reference frame and pan/zoom go strange.
    cam.lookAtTransform(Cesium.Matrix4.IDENTITY);
    v.scene.requestRender();
  };
  const btn = {
    width: "46px",
    // >= Apple HIG 44pt touch target
    height: "46px",
    display: "flex",
    "align-items": "center",
    "justify-content": "center",
    background: "rgba(8, 20, 28, 0.82)",
    border: "1px solid rgba(0, 229, 255, 0.45)",
    "border-radius": "6px",
    color: "#7df9ff",
    "font-size": "20px",
    "font-weight": "700",
    "font-family": "ui-monospace, SFMono-Regular, Menlo, monospace",
    "line-height": "1",
    "backdrop-filter": "blur(4px)",
    "-webkit-tap-highlight-color": "transparent",
    "touch-action": "manipulation",
    "user-select": "none",
    cursor: "pointer"
  };
  return _$createComponent(Show, {
    get when() {
      return props.visible;
    },
    get children() {
      var _el$ = _tmpl$(),
        _el$2 = _el$.firstChild,
        _el$3 = _el$2.nextSibling,
        _el$4 = _el$3.nextSibling,
        _el$5 = _el$4.nextSibling;
      _el$2.$$pointerdown = () => zoom(1);
      _el$3.$$pointerdown = () => zoom(-1);
      _el$4.$$pointerdown = () => tilt(+TILT_STEP);
      _el$5.$$pointerdown = () => tilt(-TILT_STEP);
      _$effect(_p$ => {
        var _v$ = btn,
          _v$2 = btn,
          _v$3 = btn,
          _v$4 = btn;
        _p$.e = _$style(_el$2, _v$, _p$.e);
        _p$.t = _$style(_el$3, _v$2, _p$.t);
        _p$.a = _$style(_el$4, _v$3, _p$.a);
        _p$.o = _$style(_el$5, _v$4, _p$.o);
        return _p$;
      }, {
        e: undefined,
        t: undefined,
        a: undefined,
        o: undefined
      });
      return _el$;
    }
  });
}
export default TouchNav;
_$delegateEvents(["pointerdown"]);